/**
 * Handler loader factory
 * Lazy-loads tool handlers on demand to reduce initial bundle size
 */

import type { Tool } from '../types/index.js';
import { logger } from '../../logger.js';

/**
 * Cache for loaded handlers to avoid re-instantiation
 */
const handlerCache = new Map<string, any>();

/**
 * Lazy-load a handler by ID
 * Uses dynamic imports to defer handler instantiation
 */
export async function loadHandler(toolId: string): Promise<any> {
  // Return cached handler if already loaded
  if (handlerCache.has(toolId)) {
    return handlerCache.get(toolId);
  }

  let handler: any;

  // Dynamically import and instantiate handlers based on tool ID
  switch (toolId) {
    case 'find_contact': {
      const { FindContactHandler } = await import('../handlers/query/find-contact.handler');
      handler = new FindContactHandler();
      break;
    }
    case 'find_company': {
      const { FindCompanyHandler } = await import('../handlers/query/find-company.handler');
      handler = new FindCompanyHandler();
      break;
    }
    case 'search_crm': {
      const { SearchCRMHandler } = await import('../handlers/query/search-crm.handler');
      handler = new SearchCRMHandler();
      break;
    }
    case 'audit_contact_timeline': {
      const { AuditContactTimelineHandler } = await import('../handlers/query/audit-contact-timeline.handler');
      handler = new AuditContactTimelineHandler();
      break;
    }
    case 'list_contacts': {
      const { ListContactsHandler } = await import('../handlers/query/list-contacts.handler');
      handler = new ListContactsHandler();
      break;
    }
    case 'edit_contact': {
      const { EditContactHandler } = await import('../handlers/edit/edit-contact.handler');
      handler = new EditContactHandler();
      break;
    }
    case 'edit_company': {
      const { EditCompanyHandler } = await import('../handlers/edit/edit-company.handler');
      handler = new EditCompanyHandler();
      break;
    }
    case 'create_contact_activity': {
      const { CreateContactActivityHandler } = await import('../handlers/edit/create-contact-activity.handler');
      handler = new CreateContactActivityHandler();
      break;
    }
    case 'find_deal': {
      const { FindDealHandler } = await import('../handlers/query/find-deal.handler');
      handler = new FindDealHandler();
      break;
    }
    case 'list_deals': {
      const { ListDealsHandler } = await import('../handlers/query/list-deals.handler');
      handler = new ListDealsHandler();
      break;
    }
    case 'search_deals': {
      const { SearchDealsHandler } = await import('../handlers/query/search-deals.handler');
      handler = new SearchDealsHandler();
      break;
    }
    case 'get_deal_analytics': {
      const { DealAnalyticsHandler } = await import('../handlers/query/deal-analytics.handler');
      handler = new DealAnalyticsHandler();
      break;
    }
    case 'get_deals_by_stage': {
      const { DealsByStageHandler } = await import('../handlers/query/deals-by-stage.handler');
      handler = new DealsByStageHandler();
      break;
    }
    case 'create_deal': {
      const { CreateDealHandler } = await import('../handlers/edit/create-deal.handler');
      handler = new CreateDealHandler();
      break;
    }
    case 'update_deal': {
      const { UpdateDealHandler } = await import('../handlers/edit/update-deal.handler');
      handler = new UpdateDealHandler();
      break;
    }
    case 'mark_deal_won': {
      const { MarkDealWonHandler } = await import('../handlers/edit/mark-deal-won.handler');
      handler = new MarkDealWonHandler();
      break;
    }
    case 'mark_deal_lost': {
      const { MarkDealLostHandler } = await import('../handlers/edit/mark-deal-lost.handler');
      handler = new MarkDealLostHandler();
      break;
    }
    case 'log_communication': {
      const { LogCommunicationHandler } = await import('../handlers/edit/log-communication.handler');
      handler = new LogCommunicationHandler();
      break;
    }
    default:
      // Return null for unknown tool IDs
      // This allows tools defined in other packages (e.g., Hub-specific tools)
      // to be skipped gracefully during handler loading
      // These tools have their own execution logic in their respective packages
      return null;
  }

  // Cache the loaded handler
  handlerCache.set(toolId, handler);
  return handler;
}

/**
 * Load multiple handlers by ID
 * Uses Promise.allSettled to handle partial failures gracefully
 */
export async function loadHandlers(toolIds: string[]): Promise<Map<string, any>> {
  const handlers = new Map<string, any>();
  const promises = toolIds.map(async (id) => {
    try {
      const handler = await loadHandler(id);
      // Only add to map if handler is not null
      // Null handlers indicate tools defined in other packages (e.g., Hub-specific tools)
      if (handler !== null) {
        handlers.set(id, handler);
      }
    } catch (error) {
      logger.error('failed to load handler for tool', error, { op: 'loadHandlers', toolId: id });
      // Continue loading other handlers instead of failing completely
      // This allows partial initialization if some handlers fail
    }
  });

  // Use allSettled instead of all to handle partial failures
  await Promise.allSettled(promises);
  return handlers;
}

/**
 * Load all handlers
 */
export async function loadAllHandlers(): Promise<Map<string, any>> {
  const allToolIds = [
    'find_contact',
    'find_company',
    'search_crm',
    'audit_contact_timeline',
    'list_contacts',
    'edit_contact',
    'edit_company',
    'create_contact_activity',
    'find_deal',
    'list_deals',
    'search_deals',
    'get_deal_analytics',
    'get_deals_by_stage',
    'create_deal',
    'update_deal',
    'mark_deal_won',
    'mark_deal_lost',
  ];
  return loadHandlers(allToolIds);
}

/**
 * Attach loaded handlers to tool definitions
 */
export async function attachHandlersToTools(toolDefinitions: Record<string, Tool>): Promise<void> {
  const toolIds = Object.keys(toolDefinitions);
  const handlers = await loadHandlers(toolIds);

  for (const [toolId, handler] of handlers) {
    if (toolDefinitions[toolId] && handler) {
      toolDefinitions[toolId].handler = handler;
    }
  }
}

/**
 * Clear handler cache (useful for testing)
 */
export function clearHandlerCache(): void {
  handlerCache.clear();
}

/**
 * Get handler cache size (useful for debugging)
 */
export function getHandlerCacheSize(): number {
  return handlerCache.size;
}
